import { useState, useRef } from 'react'
import { ArrowRight, Rss, Smartphone, Headphones } from 'lucide-react'
import Dialog from '../ui/dialog'
import { useSeriesRssUrl } from '../../api/series'
import { cn } from '../../lib/utils'

interface SubscribeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  seriesId: string
}

export function SubscribeDialog({ open, onOpenChange, seriesId }: SubscribeDialogProps) {
  const { data, isLoading } = useSeriesRssUrl(seriesId)
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const rssUrl = data?.rssUrl || ''

  const handleCopy = async () => {
    if (!rssUrl) return
    await navigator.clipboard.writeText(rssUrl)
    setCopied(true)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setCopied(false), 2000)
  }

  const handleOpenApplePodcasts = () => {
    if (!rssUrl) return
    const podcastUrl = rssUrl.replace(/^https?:\/\//, 'podcast://')
    window.open(podcastUrl, '_blank')
  }
  
  const handleOpenPocketCasts = () => {
    if (!rssUrl) return
    const podcastUrl = rssUrl.replace(/^https?:\/\//, 'pktc://subscribe/')
    window.open(podcastUrl, '_blank')
  }

  const handleOpenOvercast = () => {
    if (!rssUrl) return
    const podcastUrl = `overcast://x-callback-url/add?url=${encodeURIComponent(rssUrl)}`
    window.open(podcastUrl, '_blank')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Listen to this series"
      description="Subscribe in your favorite podcast app to get new episodes automatically."
      size="md"
      heightMode="auto"
    >
      <div className="flex flex-col gap-6 py-2">
        
        {/* Open directly options */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Quick subscribe</h3>
          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={handleOpenApplePodcasts}
              disabled={!rssUrl}
              className={cn(
                "group flex items-center justify-between p-3 rounded-xl border border-black/5 bg-black/[0.02] transition-colors text-left",
                rssUrl ? "hover:bg-black/5 hover:border-black/10 cursor-pointer" : "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-white shadow-sm text-[#872ec4]">
                                    <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-podcast"><circle cx="12" cy="11" r="1"/><path d="M11 17a1 1 0 0 1 2 0c0 .5-.34 3-.5 4.5a.5.5 0 0 1-1 0c-.16-1.5-.5-4-.5-4.5Z"/><path d="M8 14a5 5 0 1 1 8 0"/><path d="M17 18.5a9 9 0 1 0-10 0"/></svg>
                </div>
                <div>
                  <div className="font-medium text-foreground">Apple Podcasts</div>
                  <div className="text-xs text-foreground/50">iOS & macOS default</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-foreground/30 group-hover:text-foreground/60 transition-colors" />
            </button>
            
            <button
              onClick={handleOpenPocketCasts}
              disabled={!rssUrl}
              className={cn(
                "group flex items-center justify-between p-3 rounded-xl border border-black/5 bg-black/[0.02] transition-colors text-left",
                rssUrl ? "hover:bg-black/5 hover:border-black/10 cursor-pointer" : "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-white shadow-sm text-[#f13233]">
                                  <Smartphone className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-medium text-foreground">Pocket Casts</div>
                  <div className="text-xs text-foreground/50">iOS & Android</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-foreground/30 group-hover:text-foreground/60 transition-colors" />
            </button>

            <button
              onClick={handleOpenOvercast}
              disabled={!rssUrl}
              className={cn(
                "group flex items-center justify-between p-3 rounded-xl border border-black/5 bg-black/[0.02] transition-colors text-left",
                rssUrl ? "hover:bg-black/5 hover:border-black/10 cursor-pointer" : "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-white shadow-sm text-[#fc7e0f]">
                  <Headphones className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-medium text-foreground">Overcast</div>
                  <div className="text-xs text-foreground/50">iOS only</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-foreground/30 group-hover:text-foreground/60 transition-colors" />
            </button>
          </div>
        </div>

        {/* Copy RSS Link Section */}
        <div className="space-y-3 pt-2 border-t border-black/5">
          <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Use RSS URL manually</h3>
          <p className="text-sm text-foreground/60">
            For Spotify, YouTube Music, or other apps, copy the RSS link and paste it into the "Add from URL" section of your app.
          </p>
          
          <div className="flex items-stretch mt-2 rounded-xl bg-black/[0.03] border border-black/10 overflow-hidden">
            <div className="flex items-center pl-3 pr-2 text-foreground/40 border-r border-black/5 bg-black/5">
              <Rss className="h-4 w-4" />
            </div>
            <input 
              value={rssUrl} 
              readOnly 
              placeholder={isLoading ? "Loading..." : "RSS URL"}
              className="flex-1 bg-transparent px-3 py-2.5 font-mono text-xs text-foreground/80 outline-none"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button 
              type="button" 
              onClick={handleCopy}
              disabled={!rssUrl}
              className={cn(
                "px-4 py-2.5 text-sm font-medium transition-colors border-l border-black/5",
                copied ? "bg-success-50 text-success-700" : "bg-white hover:bg-black/5 text-foreground cursor-pointer"
              )}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

      </div>
    </Dialog>
  )
}
